import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import {
  setUserId,
  getUserId,
  getUserName,
  setUserName,
  getFirstName,
  setFirstName,
  getLastName,
  setLastName,
  setProfileImage,
  getProfileImage,
} from "../services/stores/userStore";

interface UserContextType {
  userId: number | null;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  setUserId: (userId: number | null) => void;
  setUserName: (userName: string | null) => void;
  setFirstName: (firstName: string | null) => void;
  setLastName: (lastName: string | null) => void;
  setProfileImage: (profileImage: string | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userId, setUserIdState] = useState<number | null>(getUserId());
  const [userName, setUserNameState] = useState<string | null>(getUserName());
  const [firstName, setFirstNameState] = useState<string | null>(getFirstName());
  const [lastName, setLastNameState] = useState<string | null>(getLastName());
  const [profileImage, setProfileImageState] = useState<string | null>(getProfileImage());

  const updateUserId = (userId: number) => {
    setUserIdState(userId); // Update React state
    setUserId(userId); // Update global store
  };

  const updateUserName = (userName: string | null) => {
    setUserNameState(userName);
    setUserName(userName);
  };
  const updateFirstName = (firstName: string) => {
    setFirstNameState(firstName); // Update React state
    setFirstName(firstName); // Update global store
  };
  const updateLastName = (lastName: string) => {
    setLastNameState(lastName); // Update React state
    setLastName(lastName); // Update global store
  };
  const updateProfileImage = (profileImage: string | null) => {
    // Bug 79975: the API returns an empty string once a user removes their photo - normalise it to
    // null so consumers have a single "no photo" value, and let null reach the store so the
    // persisted image is actually cleared rather than resurrected on the next load.
    const removedOrUrl = profileImage || null;
    setProfileImageState(removedOrUrl); // Update React state
    setProfileImage(removedOrUrl); // Update global store
  };

  useEffect(() => {
    if (userId !== null) {
      setUserId(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (userName !== null) {
      setUserName(userName);
    }
  }, [userName]);
  useEffect(() => {
    if (firstName !== null) {
      setFirstName(firstName);
    }
  }, [firstName]);
  useEffect(() => {
    if (lastName !== null) {
      setLastName(lastName);
    }
  }, [lastName]);

  useEffect(() => {
    setProfileImage(profileImage);
  }, [profileImage]);
  return (
    <UserContext.Provider
      value={{
        userId,
        setUserId: updateUserId,
        userName,
        setUserName: updateUserName,
        firstName,
        setFirstName: updateFirstName,
        lastName,
        setLastName: updateLastName,
        profileImage,
        setProfileImage: updateProfileImage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
